/** @jsxImportSource @emotion/react */
import Heading from './Heading';
import { useActiveEditor } from '../ActiveEditorContext';

import type { IEditorToolbar, ILabels } from '../types';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import FormatUnderlined from '@mui/icons-material/FormatUnderlined';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import TextAlignment from './TextAlignment';
import FontFamily from './FontFamily';
import PyDivider from '../components/generalComponents/PyDivider';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import { Fragment, useMemo, useState } from 'react';
import { PyMuiIconButton } from '../components/generalComponents';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import LineHeight from './LineHeight';
import FormatLineSpacingRoundedIcon from '@mui/icons-material/FormatLineSpacingRounded';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import IcoHighlight from '../../../../icon/icons/IcoHighlight';
import HyperLink from './HyperLink.tsx';
import TextColor from './TextColor';
import FontSize from './FontSize';
import { selectTableTextDefaultStyles, selectTextDefaultStyles } from '../../../grid/reduxStore/textDefaultStylingSlice.ts';
import { useAppSelector } from '../../../grid/reduxStore/Store.ts';
import { getParentNodeType } from '../helpers/textStyleHelpers.ts';
import { useIconsActiveFormats } from '../hooks/useIconsActiveFormats.ts';
import { AI } from './AI';
import { useTranslation } from 'react-i18next';
import { useGetTipTapToolbarStatus } from '../hooks/useGetTipTapToolbarStatus.ts';
import { useComments } from 'providers/CommentsContext.tsx';

const ToolbarWrapper = styled(Box)`
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: flex-start;
`;

export type TipTapToolbarProps = {
  documentId?: string;
  className?: string;
  toolbar?: IEditorToolbar[];
  labels?: Omit<ILabels, 'editor'>;
};

type ToolbarIcon = {
  name: string;
  icon: React.ReactNode;
  hasDividerBefore: boolean;
  toggleAction: string;
  outlinedColorWhenActive?: string;
  isDisabled?: boolean;
  checkForGeneralStyle?: boolean;
  customAction?: string;
  attribute?: string;
  valueDefault?: string;
  isUpdatingSpotStyles?: boolean;
  customComponent?: (disabled: boolean) => React.ReactNode;
};

export const toolbarIcons: ToolbarIcon[] = [
  {
    name: 'customBold',
    icon: <FormatBoldIcon />,
    hasDividerBefore: true,
    isUpdatingSpotStyles: true,
    toggleAction: 'toggleBold',
    checkForGeneralStyle: true,
    attribute: 'fontWeight',
    valueDefault: 'bold',
    customAction: 'toggleCustomBold',
  },
  {
    name: 'customItalic',
    icon: <FormatItalicIcon />,
    hasDividerBefore: false,
    toggleAction: 'toggleItalic',
    isUpdatingSpotStyles: true,
    checkForGeneralStyle: true,
    attribute: 'fontStyle',
    valueDefault: 'italic',
    customAction: 'toggleCustomItalic',
  },
  {
    name: 'customUnderline',
    icon: <FormatUnderlined />,
    hasDividerBefore: false,
    toggleAction: 'toggleUnderline',
    isUpdatingSpotStyles: true,
    checkForGeneralStyle: true,
    attribute: 'textDecoration',
    valueDefault: 'underline',
    customAction: 'toggleCustomUnderline',
  },
  {
    name: 'textColor',
    icon: <TextColor />,
    hasDividerBefore: false,
    toggleAction: '',
  },
  {
    name: 'highlight',
    icon: <IcoHighlight />,
    hasDividerBefore: false,
    toggleAction: 'toggleHighlight',
    outlinedColorWhenActive: 'rgba(0, 184, 209, 1)',
  },
  {
    name: 'align',
    icon: <FormatItalicIcon />,
    customComponent: (disabled) => <TextAlignment disabled={disabled} />,
    hasDividerBefore: true,
    toggleAction: 'toggleAlign',
  },
  {
    name: 'bulletList',
    icon: <FormatListBulletedIcon />,
    toggleAction: 'toggleBulletList',
    hasDividerBefore: false,
  },
  {
    name: 'orderedList',
    icon: <FormatListNumberedIcon />,
    toggleAction: 'toggleOrderedList',
    hasDividerBefore: false,
  },
  {
    name: 'lineheight',
    icon: <FormatLineSpacingRoundedIcon />,
    customComponent: (disabled) => <LineHeight disabled={disabled} />,
    hasDividerBefore: false,
    toggleAction: 'setLineHeight',
  },
  {
    name: 'link',
    icon: <HyperLink />,
    hasDividerBefore: true,
    toggleAction: 'link',
    customComponent: (disabled) => <HyperLink disabled={disabled} />,
  },
  {
    name: 'formatClear',
    icon: <FormatClearIcon />,
    hasDividerBefore: false,
    toggleAction: 'clearFormatting',
    isDisabled: true,
  },
];

// Icons always visible in the main toolbar row
const PRIMARY_ICON_NAMES = ['customBold', 'customItalic', 'customUnderline', 'textColor', 'highlight'];

const TipTapToolbar = ({ className }: TipTapToolbarProps) => {
  const { activeEditor: editor } = useActiveEditor();
  const { commentsEnabled: commentsEnabledFromContext } = useComments();

  const { t } = useTranslation();
  const textDefaultStyles = useAppSelector(selectTextDefaultStyles);
  const tableDefaultStyles = useAppSelector(selectTableTextDefaultStyles);
  const defaultStyles = useMemo(() => ({ ...textDefaultStyles, ...tableDefaultStyles }), [textDefaultStyles, tableDefaultStyles]);
  const isToolbarActive = useGetTipTapToolbarStatus();
  const isBlockInEditMode = !isToolbarActive || commentsEnabledFromContext;

  const activeFormats = useIconsActiveFormats(editor, defaultStyles);

  const isNarrow = useMediaQuery('(max-width:900px)');
  const [overflowAnchorEl, setOverflowAnchorEl] = useState<HTMLButtonElement | null>(null);

  const parent = editor?.state.selection.$from.parent || null;
  const hasStyleAttribute = (attribute: string, value: string | number): boolean => {
    if (!attribute || !parent) return false;

    const parentType = getParentNodeType(parent);
    const elementStyle = defaultStyles[parentType];
    const attributeValue = elementStyle?.[attribute];

    return attributeValue === value;
  };

  const handleIconClick = (
    customAction?: string,
    toggleAction?: string,
    name?: string,
    valueDefault?: string,
    attribute?: string,
    checkForGeneralStyle?: boolean
  ) => {
    if (!editor || !toggleAction || !name) return;

    const shouldUseCustom = valueDefault && attribute && checkForGeneralStyle && hasStyleAttribute(attribute, valueDefault) && customAction;

    if (shouldUseCustom) {
      editor.chain().focus()[customAction](activeFormats[name], name).run();
    } else {
      editor.chain().focus()[toggleAction]().run();
    }
  };

  const renderIcon = (icon: ToolbarIcon, skipLeadingDivider = false) => {
    const {
      name,
      checkForGeneralStyle,
      customAction,
      attribute,
      isDisabled,
      toggleAction,
      valueDefault,
      hasDividerBefore,
      customComponent,
      outlinedColorWhenActive,
    } = icon;
    const isButtonDisabled = (isDisabled && editor?.state?.selection?.empty) || isBlockInEditMode;
    const showDivider = hasDividerBefore && !skipLeadingDivider;

    if (customComponent) {
      return (
        <Fragment key={name}>
          {showDivider && <PyDivider />}
          {customComponent(isButtonDisabled)}
        </Fragment>
      );
    }

    return (
      <Fragment key={name}>
        {showDivider && <PyDivider />}
        <PyMuiIconButton
          attributeName={name}
          disabled={isButtonDisabled}
          onClick={() => handleIconClick(customAction, toggleAction, name, valueDefault, attribute, checkForGeneralStyle)}
          isActive={activeFormats[name]}
          outlinedColorWhenActive={outlinedColorWhenActive}
        >
          {icon.icon}
        </PyMuiIconButton>
      </Fragment>
    );
  };

  const primaryIcons = toolbarIcons.filter((icon) => PRIMARY_ICON_NAMES.includes(icon.name));
  const overflowIcons = toolbarIcons.filter((icon) => !PRIMARY_ICON_NAMES.includes(icon.name));

  return (
    <Tooltip
      arrow
      classes={{ tooltip: 'py-custom-tooltip' }}
      title={isBlockInEditMode && t('editor.toolbar.toolbar_tooltip')}
      placement="top"
    >
      <ToolbarWrapper data-testid="tiptap-toolbar" className={className}>
        <PyDivider />
        <Heading disabled={isBlockInEditMode} editor={editor} />
        <PyDivider />
        <FontFamily disabled={isBlockInEditMode} editor={editor} />
        <PyDivider />
        <FontSize disabled={isBlockInEditMode} editor={editor} />
        <Box display={'flex'} flexDirection={'row'} gap={'1px'}>
          {(isNarrow ? primaryIcons : toolbarIcons).map((icon) => renderIcon(icon))}
        </Box>

        {isNarrow ? (
          <>
            <PyMuiIconButton attributeName="overflow-menu" onClick={(e) => setOverflowAnchorEl(e.currentTarget)}>
              <MoreHorizRoundedIcon />
            </PyMuiIconButton>
            <Popover
              open={Boolean(overflowAnchorEl)}
              anchorEl={overflowAnchorEl}
              onClose={() => setOverflowAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              disableScrollLock
              slotProps={{ paper: { sx: { borderRadius: '8px' } } }}
            >
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap="1px"
                padding="4px"
                data-testid="tiptap-toolbar-overflow-panel"
              >
                {overflowIcons.map((icon, index) => renderIcon(icon, index === 0 /* skip leading divider on the first overflow item */))}

                <PyDivider />
                <AI disabled={isBlockInEditMode || commentsEnabledFromContext} editor={editor} />
              </Box>
            </Popover>
          </>
        ) : (
          <>
            <PyDivider />
            <AI disabled={isBlockInEditMode || commentsEnabledFromContext} editor={editor} />
          </>
        )}
      </ToolbarWrapper>
    </Tooltip>
  );
};

export default TipTapToolbar;
